import { useState, type Dispatch, type SetStateAction } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  Divider,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import type { AssessmentId } from "@thalamos/haven-common";

import {
  checkboxCellStyle,
  countTextStyle,
  gutter,
  ShareableRow,
  ShareableStacked,
  shareColumns,
  StackedChoice,
  useStackedLayout,
  type ShareableAssessment,
} from "./share_step_parts.js";

/** How many assessments the step lists before Show older. */
const listedAtFirst = 5;

/**
 * The first step of sharing: ticking which finished assessments to share,
 * the latest `listedAtFirst` listed until Show older. Opens with the older
 * ones shown where one of them is already chosen, so nothing chosen is out of
 * sight.
 */
export const ShareChooseStep = ({
  assessments,
  chosen,
  onChosenChange,
  onCancel,
  onNext,
}: {
  /** The finished assessments the sharer may read, newest first. */
  assessments: readonly ShareableAssessment[];
  chosen: ReadonlySet<AssessmentId>;
  onChosenChange: Dispatch<SetStateAction<ReadonlySet<AssessmentId>>>;
  onCancel: () => Promisable<void>;
  onNext: () => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const { ref, stacked } = useStackedLayout();
  const [showingOlder, setShowingOlder] = useState(() =>
    assessments
      .slice(listedAtFirst)
      .some(({ assessment }) => chosen.has(assessment.id)),
  );

  const listed = showingOlder
    ? assessments
    : assessments.slice(0, listedAtFirst);
  const chosenCount = assessments.filter(({ assessment }) =>
    chosen.has(assessment.id),
  ).length;
  const allChosen =
    assessments.length > 0 && chosenCount === assessments.length;

  const toggle = (id: AssessmentId) => {
    onChosenChange((before) => {
      const next = new Set(before);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = (
    <Checkbox
      checked={allChosen}
      indeterminate={chosenCount > 0 && !allChosen}
      slotProps={{
        input: { "aria-label": t("shareWithGuest.choose.selectAll") },
      }}
      sx={{ p: 0 }}
      onChange={() => {
        onChosenChange(
          allChosen
            ? new Set()
            : new Set(assessments.map(({ assessment }) => assessment.id)),
        );
      }}
    />
  );

  const olderControl =
    assessments.length > listedAtFirst ? (
      <Stack
        direction="row"
        spacing={2}
        alignItems="baseline"
        sx={{ px: gutter, py: 1.5 }}
      >
        <Typography variant="body2" sx={countTextStyle}>
          {t("shareWithGuest.choose.shown", {
            shown: listed.length,
            total: assessments.length,
          })}
        </Typography>
        {showingOlder ? null : (
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={() => {
              setShowingOlder(true);
            }}
          >
            {t("shareWithGuest.choose.showOlder")}
          </Link>
        )}
      </Stack>
    ) : null;

  return (
    <>
      {/* No padding: each row carries the gutter itself so the list runs
      edge to edge, and a sticky heading sticks inside its scroller's
      padding, where rows would show through. */}
      <DialogContent dividers sx={{ p: 0 }}>
        <Box ref={ref}>
          {assessments.length === 0 ? (
            <Alert severity="info" sx={{ m: gutter }}>
              {t("shareWithGuest.choose.none")}
            </Alert>
          ) : stacked ? (
            <Stack divider={<Divider />}>
              <StackedChoice checkbox={selectAll}>
                <Typography variant="body2">
                  {t("shareWithGuest.choose.selectAll")}
                </Typography>
              </StackedChoice>
              {listed.map((offered) => (
                <ShareableStacked
                  key={offered.assessment.id}
                  offered={offered}
                  chosen={chosen.has(offered.assessment.id)}
                  onToggle={() => {
                    toggle(offered.assessment.id);
                  }}
                />
              ))}
              {olderControl}
            </Stack>
          ) : (
            <>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={checkboxCellStyle}>{selectAll}</TableCell>
                    {shareColumns.map((column) => (
                      <TableCell
                        key={column}
                        sx={column === "finished" ? { pr: gutter } : {}}
                      >
                        {t(`personPage.assessments.history.column.${column}`)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listed.map((offered) => (
                    <ShareableRow
                      key={offered.assessment.id}
                      offered={offered}
                      chosen={chosen.has(offered.assessment.id)}
                      onToggle={() => {
                        toggle(offered.assessment.id);
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
              {olderControl}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        disableSpacing
        sx={{ p: gutter, flexWrap: "wrap", gap: 1, rowGap: 1.5 }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="baseline"
          sx={{ mr: "auto", flexBasis: stacked ? "100%" : "auto" }}
        >
          <Typography variant="body2" aria-live="polite" sx={countTextStyle}>
            {t("shareWithGuest.choose.chosen", {
              count: chosenCount,
              total: assessments.length,
            })}
          </Typography>
          {chosenCount === 0 ? null : (
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              onClick={() => {
                onChosenChange(new Set());
              }}
            >
              {t("shareWithGuest.choose.clear")}
            </Link>
          )}
        </Stack>
        <Button
          variant="outlined"
          onClick={() => {
            void onCancel();
          }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={chosenCount === 0}
          onClick={() => {
            void onNext();
          }}
        >
          {t("shareWithGuest.choose.next")}
        </Button>
      </DialogActions>
    </>
  );
};
